import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {config} from 'config/env';
import i18n from 'config/i18n/i18n';
import {
  ComplexPromotionIconMap,
  LOYALTY_AND_CAMPAIGN_CDN_FILE_NAMES,
  PROMOTION_SUGGESTION_TARGET_UNIT
} from 'constants/recommendation';
import {useAppSelector} from 'hooks';
import {Product, ProductSearchResult} from 'types';
import {CollectionType, ComplexPromotion3P} from 'types/recommedation-types/recommendations';
import {formatPriceWithoutDecimal} from 'utils';
import {getProPointPromotions} from 'utils/product/getProPointPromotions';
import {ProductModel} from 'utils/product/types';
import {
  buildStrapiCDNURL,
  ComplexPromotionType,
  determinePromotionType,
  getRm3PromotionRewardType,
  PROMOTION_REWARD,
  PROPOINT_COLLECTION
} from 'utils/recommendation-utils/proPointsPromotion.util';

export const RIBBONS_TITLE = {
  POINT: 'point',
  LOYALTY: 'loyalty'
};

export const usePromotionRibbon = (product: Product | ProductSearchResult) => {
  const {complexPromotion} = useAppSelector((state) => state.recommendations);
  const isComplexPriority = config.RECOM_LOYALTY_COMPLEX_PROMOTION_PRIORITY;
  const isLoyaltyCampaignPromoTagV2Enabled = config.RECOM_LOYALTY_CAMPAIGN_PROMO_TAG_V2_ENABLED;

  const {t} = useTranslation();
  const locale = i18n.language;
  const isThai = locale === 'th';
  const {promotionSuggestion, proPointPromotionsApplicable, totalPromotions} = useMemo(() => {
    return getProPointPromotions(product as unknown as ProductModel);
  }, [product]);

  let title = '';
  let type = PROPOINT_COLLECTION;
  let iconUrl = buildStrapiCDNURL(LOYALTY_AND_CAMPAIGN_CDN_FILE_NAMES.proPointIcon);

  const promotion = complexPromotion?.[product.sku ?? ''] ??
    complexPromotion?.[product.makroId ?? product.id] ?? {promoAdvice: [], loading: false};
  const isComplexPromotionEnabled = config.IS_COMPLEX_PROMOTION_ENABLED;
  let hasComplexPromotion = false;
  const hasProPointPromotion = product.collections?.some((p) => {
    return p.type === CollectionType.PROPOINT_PROMOTION;
  });

  const flatComplexPromotion3P: ComplexPromotion3P[] = [];
  for (const variant of product.variants || []) {
    flatComplexPromotion3P.push(...(variant?.complexPromotion3P || []));
  }

  if (promotion && isComplexPromotionEnabled) {
    hasComplexPromotion = promotion.promoAdvice.length > 0 || flatComplexPromotion3P.length > 0;
  }

  const isPlpProPointPromotionEnabled = proPointPromotionsApplicable && config.RECOM_PLP_PROPOINT_PROMOTION_ENABLED;
  const isAsPlpProPointPromotionEnabled =
    proPointPromotionsApplicable && config.RECOM_AS_PLP_PROPOINT_PROMOTION_ENABLED;

  const isAsComplexPromotionEnabled = hasComplexPromotion && config.RECOM_AS_PLP_PROPOINT_PROMOTION_ENABLED;

  const getComplexPromotionTitle = () => {
    if (promotion.promoAdvice.length > 0) {
      const [complexPromotion] = promotion.promoAdvice;

      title = complexPromotion.title;
      type = complexPromotion.type;
      if (isLoyaltyCampaignPromoTagV2Enabled) {
        iconUrl = buildStrapiCDNURL(ComplexPromotionIconMap[complexPromotion.subType]);
        const promoType = getRm3PromotionRewardType(complexPromotion?.meta?.promoType || '');
        const promotionType = determinePromotionType(
          true, // v2 is support bogo by default
          product.displayPrice,
          complexPromotion.meta.rewardValue,
          complexPromotion.meta.targetValue,
          complexPromotion.subType
        );
        if (promotionType === ComplexPromotionType.BOGO) {
          title = t('complexPromotions.bogoPromotion');
        } else {
          const rewardValue =
            promoType === PROMOTION_REWARD.PERCENTAGE_OFF
              ? `${complexPromotion.meta.rewardValue}%`
              : `${complexPromotion.meta.rewardValue}฿`;

          title = t('labels.promotion.discount', {
            rewardValue: rewardValue
          });
        }
      }
    }

    if (flatComplexPromotion3P.length > 0) {
      const [promo] = flatComplexPromotion3P;
      title = promo.title;
      type = ComplexPromotionType.SELLER_PROMO;
      if ((product as Product).brandDetails?.logoUrl) {
        iconUrl = (product as Product)?.brandDetails?.logoUrl || '';
      }
      if (isLoyaltyCampaignPromoTagV2Enabled) {
        const rewardValue =
          promo.rewardType === PROMOTION_REWARD.BATH_OFF_3P ? `${promo.rewardValue}฿` : `${promo.rewardValue}%`;

        title = t('labels.promotion.discount', {
          rewardValue: rewardValue
        });
      }
    }
  };

  if (proPointPromotionsApplicable) {
    const suggestion = promotionSuggestion as {
      targetType: string;
      rewardType: string;
      targetValue: number;
      rewardValue: number;
    };
    const promotionTextKey =
      suggestion.targetType === PROMOTION_SUGGESTION_TARGET_UNIT
        ? 'labels.proPointSuggestionUnits'
        : 'labels.proPointSuggestion';

    title = t(promotionTextKey, {
      targetValue: formatPriceWithoutDecimal(promotionSuggestion.targetValue),
      rewardValue: promotionSuggestion.rewardValue
    });
    if (isLoyaltyCampaignPromoTagV2Enabled) {
      title = t('labels.promotion.points', {
        rewardValue: promotionSuggestion.rewardValue
      });
    }
  } else {
    //check if complex promotion exist
    if (hasComplexPromotion) {
      getComplexPromotionTitle();
    }
  }

  if (isComplexPriority && hasComplexPromotion) {
    getComplexPromotionTitle();
  }

  const promotionCountProcessed = totalPromotions + promotion.promoAdvice.length + flatComplexPromotion3P.length;

  let displayRibbon = !promotion.loading;
  const promotionCollectionCount =
    product.collections?.filter((c) => {
      return c.type === CollectionType.PROMOTION;
    }).length || 0;
  const isPointTitle = product?.collections?.some((p) => {
    if (p.type !== CollectionType.PROMOTION) {
      return false;
    }

    const enTitle = isThai ? p.titleAlt : p.title;

    return enTitle?.toLowerCase().includes(RIBBONS_TITLE.POINT);
  });
  if (promotionCollectionCount) {
    //has manual promotion check condition
    //case 1 has automate and complex
    if (hasProPointPromotion && hasComplexPromotion) {
      displayRibbon = false;
    }

    //case 2 has suggestion don't have complex
    if (hasProPointPromotion && !hasComplexPromotion) {
      //check if it has point title if yes we need to hide it

      if (isPointTitle) {
        displayRibbon = false;
      } else {
        displayRibbon = true;
      }
    }

    //case 3 has complex don't have suggestion
    if (!hasProPointPromotion && hasComplexPromotion) {
      //show if contain point
      if (isPointTitle) {
        displayRibbon = true;
      } else {
        displayRibbon = false;
      }
    }

    //case 4 has don't have any
    if (!hasProPointPromotion && !hasComplexPromotion) {
      displayRibbon = true;
    }
  }

  return {
    title,
    totalPromotions: promotionCountProcessed,
    complexPromotionLoading: promotion?.loading,
    isPlpProPointPromotionEnabled,
    isAsPlpProPointPromotionEnabled,
    type,
    hasComplexPromotion,
    isAsComplexPromotionEnabled,
    displayRibbon,
    iconUrl
  };
};
